import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";

import api from "@/lib/api";

function Ico({ name, className }) {
  const map = {
    "graduation-cap": Icons.GraduationCap, "zap": Icons.Zap, "sprout": Icons.Sprout,
    "building": Icons.Building2, "briefcase": Icons.Briefcase, "shield": Icons.Shield,
  };
  const C = map[name] || Icons.Star;
  return <C className={className} />;
}

export default function Vision() {
  const [data, setData] = useState({ meta: {}, pillars: [], projects: [] });
  useEffect(() => { api.get("/content/vision").then((r) => setData(r.data)).catch(() => {}); }, []);
  const meta = data.meta || {};

  return (
    <div>
      <section className="topo-bg text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <span className="eyebrow text-secondary">Wadada's Vision · {meta.period}</span>
          <h1 className="font-heading font-black text-4xl md:text-6xl mt-3 leading-none">{meta.title || "The Wadada HEARTS Agenda"}</h1>
          <p className="mt-5 text-lg text-emerald-50 max-w-3xl leading-relaxed">{meta.intro}</p>
          {meta.tagline && <p className="mt-4 text-secondary font-semibold text-base md:text-lg">{meta.tagline}</p>}
        </div>
      </section>

      {/* HEARTS ACRONYM STRIP */}
      <section className="max-w-7xl mx-auto px-6 -mt-10 relative z-20">
        <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 p-6 flex flex-wrap items-center justify-center gap-3 md:gap-6">
          {data.pillars.map((p, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="flex items-center gap-2">
              <span className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary text-white font-heading font-black text-xl md:text-2xl flex items-center justify-center">{p.letter}</span>
              <span className="font-semibold text-sm md:text-base text-slate-800 hidden sm:inline">{p.title.split(" ")[0]}</span>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <span className="eyebrow">Six Pillars</span>
        <h2 className="font-heading font-extrabold text-3xl md:text-4xl mt-2 mb-10">The HEARTS Pillars</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.pillars.map((p, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 hover:shadow-lg hover:-translate-y-1 transition-all border-t-4 border-t-primary relative overflow-hidden">
              <div className="absolute right-4 top-3 font-heading font-black text-6xl text-primary/5">{p.letter}</div>
              <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center mb-4"><Ico name={p.icon} className="w-6 h-6 text-primary" /></div>
              <h3 className="font-heading font-bold text-lg">{p.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-muted/50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <span className="eyebrow">Flagship Commitments</span>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl mt-2 mb-10">Key Programmes of the Agenda</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {data.projects.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: i % 2 ? 20 : -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                data-testid={`project-${i}`}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex gap-5">
                <div className="shrink-0 w-14 h-14 rounded-xl topo-bg flex items-center justify-center"><Icons.Award className="w-7 h-7 text-secondary" /></div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold uppercase bg-primary/10 text-primary px-2 py-0.5 rounded-full">{p.category}</span>
                    <span className="text-xs text-muted-foreground">{p.year}</span>
                  </div>
                  <h3 className="font-heading font-bold text-lg mt-2">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {meta.motto && (
        <section className="topo-bg">
          <div className="max-w-5xl mx-auto px-6 py-16 text-center text-white">
            <Icons.Heart className="w-10 h-10 mx-auto text-secondary" />
            <p className="mt-4 font-heading font-black text-2xl md:text-4xl leading-tight">{meta.motto}</p>
            <p className="mt-3 text-emerald-100 font-semibold">In God We Trust.</p>
          </div>
        </section>
      )}
    </div>
  );
}
